import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfBrush, ETextAlign } from '../../pdf/pdf';
import { ILayoutImageSingleGen } from '../layoutImageSingle';
import { IItemCount } from '@educraft/base';
import { OptsImageSingle } from '../optsImageSingle';
import { GUtil } from '../../pdf/gutil';
import { fixGenArray } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';

const ROWS_PER_PAGE = 5;

/**
 * Obrazek__Rovnice — worksheet combining uploaded images with generated equations.
 *
 * Each row shows:
 *   [ image ] [ equation text ]  [ _____ ] ← answer box
 *
 * Images cycle through the provided list. Equations are produced by the
 * content generator. Both provide visual context and math practice.
 *
 * Port/new: EduFrame.ImgSingle.Generators.Obrazek__Rovnice
 */
export class ObrazekRovnice implements ILayoutImageSingleGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 5;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsImageSingle): Promise<void> {
    const margin = PdfUnit.FromMillimeters(10);
    const gap = PdfUnit.FromMillimeters(3);
    const rowHeight = (PdfSize.A4.height - 2 * margin) / ROWS_PER_PAGE;
    const imgWidth = rowHeight * 0.85;
    const answerWidth = imgWidth;
    const eqWidth = PdfSize.A4.width - 2 * margin - 2 * imgWidth - 3 * gap;

    const totalRows = opts.list.length;
    const totalPages = Math.ceil(totalRows / ROWS_PER_PAGE);

    for (let pageIdx = 0; pageIdx < totalPages; pageIdx++) {
      const page = pdf.addPage();

      for (let row = 0; row < ROWS_PER_PAGE; row++) {
        const globalIdx = pageIdx * ROWS_PER_PAGE + row;
        if (globalIdx >= totalRows) break;

        const y = margin + row * rowHeight;
        const imgItem = opts.list.length > 0 ? opts.list[globalIdx % opts.list.length] : null;
        const eq = opts.generator.generate(opts.data);
        const texts = fixGenArray(eq.texts);

        // Image cell
        const imgRect = new PdfRect(margin, y, imgWidth, rowHeight);
        if (imgItem?.image) {
          page.drawImageCenter(imgItem.image, imgRect.deflate(2));
        }
        page.drawRectangle(GUtil.basePen, null, imgRect);

        // Equation cell
        const eqRect = new PdfRect(margin + imgWidth + gap, y, eqWidth, rowHeight);
        const font = page.adjustFont(rowHeight * 0.45, texts, GUtil.baseFont);
        page.drawStrings(
          texts,
          font,
          PdfBrush.Black,
          null,
          eqRect.deflate(3),
          ETextAlign.CenterLeft
        );
        page.drawRectangle(GUtil.basePen, null, eqRect);

        // Answer box (empty — student fills in)
        const ansRect = new PdfRect(
          margin + imgWidth + gap + eqWidth + gap,
          y,
          answerWidth,
          rowHeight
        );
        page.drawRectangle(GUtil.basePen, null, ansRect);
      }
    }
  }
}
